/**
 * Favourites — a shortlist of product handles held in localStorage.
 *
 * Ported from /var/www/arthursmilkbar/public/assets/js/favourites.js on d1,
 * which is the source of truth for this behaviour. The logic is unchanged; only
 * the "what the Shopify theme will need" banner has been rewritten, because
 * everything it asked for is now done and it reads better as a record of how
 * this file is wired than as a list of instructions.
 *
 *   AMBFavourites.toggle(handle)  -> boolean, the new state
 *   AMBFavourites.has(handle)     -> boolean
 *   AMBFavourites.list()          -> array of handles, a copy
 *   AMBFavourites.count()         -> number
 *   AMBFavourites.clear()         -> void
 *   AMBFavourites.apply(root)     -> paint state onto the hearts under root
 *
 * Fires 'amb:favourites:change' on document after every mutation, with
 * detail { handles, count }. The page listens for that rather than being called
 * back, so the grid, the badges and the empty state cannot drift out of step
 * with the store.
 *
 * WHY localStorage AND NOT sessionStorage, AND NOT A COOKIE. A catering
 * shortlist is built across visits — a customer collects items over days before
 * ordering — so it has to outlive the tab. Only the browser ever reads it; a
 * cookie would be attached to every request to every asset on the domain, which
 * is weight on the wire for data the server has no use for.
 *
 * WHY HANDLES. The store holds product handles, never titles or grid indexes.
 * A title is copy and gets edited; an index moves the moment the collection is
 * reordered or filtered. A handle is the stable identity of the product and is
 * what the Shopify side keys on too, so a shortlist saved today still resolves
 * after a merchandising pass.
 *
 * READ ONCE, WRITE THROUGH. The list is parsed out of storage a single time and
 * then held in memory. Every read after that is an array lookup, so a grid of
 * any size costs one parse rather than one per card. Mutations update memory
 * first and then persist, so the UI never waits on storage.
 *
 * EVERY READ IS GUARDED. Storage can be absent, disabled, full, or hold
 * something that is not the JSON array we left there — another script on the
 * domain, a half-finished write, a user editing devtools. Anything that is not
 * an array of strings degrades to an empty list rather than throwing, because a
 * corrupt shortlist must not take the page down with it.
 *
 * PRIVATE MODE. Safari's private mode has historically thrown on setItem even
 * though localStorage exists. A failed write is caught, the store drops to
 * session-only, and the feature keeps working for the life of the tab instead
 * of breaking the page. Nothing tells the reader, because from where they sit
 * the hearts still work; they simply do not survive the tab closing.
 *
 * ---- HOW THIS IS WIRED INTO THE THEME ----
 * On a Shopify section the grid is re-rendered by the Section Rendering API,
 * which REPLACES the grid element wholesale on every filter, sort or paginate.
 * Anything bound to a card, or to the grid itself, dies with it. So:
 *
 *   1. Delegation is bound to `document`, not to the grid, and it is bound once
 *      when this file loads. Nothing needs rebinding after a section render.
 *      Do not move the listener onto the grid element.
 *   2. sections/amb-catering.liquid calls AMBFavourites.apply() explicitly over
 *      the finished grid, immediately after the markup is in the DOM. The
 *      MutationObserver below already covers the swap, but the explicit call is
 *      synchronous and so cannot paint an unfavourited heart for a frame first.
 *   3. Each heart carries data-fav-handle="{{ product.handle }}" and is
 *      rendered with aria-pressed="false"; apply() corrects it. Liquid cannot
 *      know the pressed state — it lives in the browser, not in the section
 *      HTML.
 *   4. The section marks a stable ancestor of the grid with data-fav-root, so
 *      apply() with no argument can find it. It is deliberately NOT the element
 *      the Section Rendering API swaps.
 *   5. Section HTML is cached by the CDN. Favourite state is never rendered
 *      into it server-side, or one reader's shortlist would be served to the
 *      next.
 */
window.AMBFavourites = (function () {
    'use strict';

    var KEY = 'amb:favourites:v1';
    var HEART = '[data-fav-handle]';

    var mem = null;          // the list, once read
    var persistent = true;   // false once a write has failed

    /* Storage itself can throw on access, not only on use: a sandboxed frame
       raises SecurityError just for touching window.localStorage. */
    function store() {
        try {
            return window.localStorage;
        } catch (e) {
            return null;
        }
    }

    /* Anything that is not an array of non-empty strings is treated as absent.
       Duplicates are collapsed, because a double write must not double a
       count that the badge reports to the reader. */
    function sanitise(value) {
        if (!Array.isArray(value)) return [];
        var seen = Object.create(null);
        var out = [];
        for (var i = 0; i < value.length; i++) {
            var h = value[i];
            if (typeof h !== 'string') continue;
            h = h.trim();
            if (h === '' || seen[h]) continue;
            seen[h] = true;
            out.push(h);
        }
        return out;
    }

    function load() {
        if (mem !== null) return mem;
        mem = [];
        var s = store();
        if (!s) { persistent = false; return mem; }
        var raw;
        try {
            raw = s.getItem(KEY);
        } catch (e) {
            persistent = false;
            return mem;
        }
        if (raw === null || raw === '') return mem;
        try {
            mem = sanitise(JSON.parse(raw));
        } catch (e) {
            /* Left in place rather than deleted. It is not ours to destroy, and
               the next successful write replaces it anyway. */
            mem = [];
        }
        return mem;
    }

    function save() {
        var s = store();
        if (!s) { persistent = false; return; }
        try {
            s.setItem(KEY, JSON.stringify(mem));
            persistent = true;
        } catch (e) {
            /* Quota, or private mode refusing the write. The in-memory list is
               already correct, so the tab carries on working unchanged. */
            persistent = false;
        }
    }

    /* Repaint, then tell the page. The repaint is here rather than at the call
       sites because the store can be mutated from places that have no heart in
       hand: the clear control in the toolbar, another tab, or any future caller
       of the API. Leaving it to the caller is what let "clear favourites" empty
       the store while all three hearts stayed filled — the store was correct and
       the only thing on screen saying otherwise was every card.

       apply() before dispatch, so a listener that reads the DOM sees the state
       the event is announcing rather than the one before it. */
    function announce() {
        apply();
        var detail = { handles: list(), count: mem.length, persistent: persistent };
        document.dispatchEvent(new CustomEvent('amb:favourites:change', { detail: detail }));
    }

    function has(handle) {
        if (typeof handle !== 'string' || handle === '') return false;
        return load().indexOf(handle) !== -1;
    }

    function list() { return load().slice(); }

    function count() { return load().length; }

    function toggle(handle) {
        if (typeof handle !== 'string' || handle === '') return false;
        load();
        var i = mem.indexOf(handle);
        if (i === -1) mem.push(handle); else mem.splice(i, 1);
        save();
        announce();
        return i === -1;
    }

    function clear() {
        load();
        if (mem.length === 0) return;
        mem = [];
        save();
        announce();
    }

    function mark(el) {
        el.setAttribute('aria-pressed', has(el.getAttribute('data-fav-handle')) ? 'true' : 'false');
    }

    /* Paints the store onto the DOM. Idempotent, so it is safe to call after
       every section render, and cheap: one array lookup per heart. */
    function apply(root) {
        var scope = root || document.querySelector('[data-fav-root]') || document;
        var hearts = scope.querySelectorAll ? scope.querySelectorAll(HEART) : [];
        for (var i = 0; i < hearts.length; i++) mark(hearts[i]);
        if (scope.nodeType === 1 && scope.matches && scope.matches(HEART)) mark(scope);
    }

    /* ---- NO FLASH WHILE THE GRID STREAMS IN ----
       A single apply() after the grid is not enough, and measuring proved it:
       the parser yields part way through a long grid, so the browser paints the
       cards it has before the script at the end of the grid ever runs. Hearts
       that should have been full were painted empty in 6 of 66 sampled frames.

       So the correction is attached to the arrival of the nodes rather than to
       the end of the document. MutationObserver callbacks are delivered at the
       microtask checkpoint, which happens BEFORE the rendering steps, so a card
       parsed in one chunk is corrected before the paint that would have shown
       it. The observer is started the moment this file executes, which is why
       the tag has to sit above the grid rather than below it, and why it is
       loaded with a plain script tag rather than deferred.

       Once parsing is done the wide observer is dropped for a narrow one on the
       favourites root, which is the one that matters for the Shopify section
       swap. Watching the whole document for the life of the page would be a
       standing cost for a job that only lasts as long as the parse. */
    var streamWatcher = null;
    function watchStream() {
        if (typeof MutationObserver === 'undefined') return;
        streamWatcher = new MutationObserver(function (records) {
            for (var i = 0; i < records.length; i++) {
                var added = records[i].addedNodes;
                for (var j = 0; j < added.length; j++) {
                    if (added[j].nodeType === 1) apply(added[j]);
                }
            }
        });
        streamWatcher.observe(document.documentElement, { childList: true, subtree: true });
    }

    /* ---- DELEGATION ----
       Bound to document, once, at load. The grid is replaced wholesale by the
       Section Rendering API on the Shopify side, so a listener on the grid or on
       a card would be discarded with it and the hearts would go dead after the
       first filter. document outlives every swap. */
    document.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest(HEART) : null;
        if (!btn) return;
        e.preventDefault();
        var handle = btn.getAttribute('data-fav-handle');
        var on = toggle(handle);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    /* A second safety net for the swap, for the case where the theme forgets to
       call apply(). Watches the stable ancestor rather than the grid, since the
       grid is the thing being replaced. */
    function observe() {
        if (streamWatcher) { streamWatcher.disconnect(); streamWatcher = null; }
        var root = document.querySelector('[data-fav-root]');
        if (!root || typeof MutationObserver === 'undefined') return;
        new MutationObserver(function () { apply(root); }).observe(root, {
            childList: true,
            subtree: true
        });
    }

    /* Another tab changed the shortlist. The storage event only fires in the
       tabs that did NOT make the change, which is exactly the set that needs to
       catch up. Memory is dropped so the next read re-parses. */
    window.addEventListener('storage', function (e) {
        if (e.key !== KEY) return;
        mem = null;
        load();
        apply();
        announce();
    });

    /* Started here, at file execution, not on DOMContentLoaded: by then the grid
       has already been parsed and painted and there is nothing left to catch. */
    if (document.readyState === 'loading') {
        watchStream();
        document.addEventListener('DOMContentLoaded', observe);
    } else {
        observe();
    }

    return {
        toggle: toggle,
        has: has,
        list: list,
        count: count,
        clear: clear,
        apply: apply,
        isPersistent: function () { load(); return persistent; },
        KEY: KEY
    };
})();
